import gsap from 'gsap';
import ScrollTrigger from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

export class ScrollManager {
    constructor(world) {
        this.world = world;
        this.initScroll();
        this.initKeyboard();
        this.initTouch();
    }

    initScroll() {
        // Wait for world to be ready
        const checkReady = () => {
            if (this.world.totalLength) {
                this.setupTrigger();
            }
        };

        const onWorldUpdate = () => {
            this.refreshScroll();
        };

        if (this.world.totalLength) checkReady();

        window.addEventListener('world-ready', checkReady);
        window.addEventListener('world-updated', onWorldUpdate); // Listen for background loads

        // We don't strictly need dom-ready anymore for height, but good to keep sync
        window.addEventListener('dom-ready', () => {
            if (this.trigger) this.trigger.refresh();
        });
    }

    refreshScroll() {
        if (!this.world.totalLength) return;

        const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

        // Mobile Sensitivity: 
        // Desktop: 500px per World Meter
        // Mobile: 150px per World Meter (Very fast)
        // Mobile sensitivity adjusted for "more power" (faster traversal)
        const sensitivity = isMobile ? 100 : 200;

        // Update Scroll Height
        const scrollHeight = this.world.totalLength * sensitivity;
        document.body.style.height = `${scrollHeight}px`;

        // Refresh ScrollTrigger to catch new body height
        if (this.trigger) this.trigger.refresh();
    }

    setupTrigger() {
        this.refreshScroll(); // Set initial height

        this.startZ = 5;
        // this.endZ is dynamic (calculated from world.totalLength)

        this.currentIndex = -1;

        // Create the main scroll trigger
        this.trigger = ScrollTrigger.create({
            trigger: "body",
            start: "top top",
            end: "bottom bottom",
            scrub: 1,
            onUpdate: (self) => {
                // Calculate EndZ dynamically
                const endZ = -this.world.totalLength + 1;

                const zPos = this.startZ - (self.progress * (this.startZ - endZ));
                this.world.camera.position.z = zPos;

                this.checkActiveSection(zPos);
                this.checkAutoScroll(zPos, self.direction);
            }
        });

        // Trigger initial check immediately
        this.checkActiveSection(this.startZ);

        // Handle Deep Linking
        this.handleDeepLink();

        // Listen for hash changes
        window.addEventListener('hashchange', () => this.handleDeepLink());
    }

    checkActiveSection(zPos) {
        if (this.world.sections && this.world.sections.length > 0) {
            // Logic similar to Overlay.js but stricter
            // Find closest section
            let newIndex = 0;
            // Simple distance check: which startZ is closest?
            // Or "Current" as in "Camera is within [startZ, startZ + length]"?
            // Overlay uses: start <= current + 2
            // Let's use simple threshold logic:
            for (let i = 0; i < this.world.sections.length; i++) {
                const secStart = -this.world.sections[i].startZ;
                if (zPos <= secStart + 5) { // +5 buffer (approaching section)
                    newIndex = i;
                }
            }

            if (newIndex !== this.currentIndex) {
                console.log(`[ScrollManager] Change Section: ${this.currentIndex} -> ${newIndex}`);
                if (this.currentIndex >= 0 && this.world.sections[this.currentIndex]) {
                    this.world.sections[this.currentIndex].setActive(false);
                }
                if (newIndex >= 0 && this.world.sections[newIndex]) {
                    console.log(`[ScrollManager] Activating Section ${newIndex}`);
                    this.world.sections[newIndex].setActive(true);
                }
                this.currentIndex = newIndex;
            }
        }
    }

    checkAutoScroll(zPos, direction) {
        if (!this.world.sections || this.isAutoScrolling || this.isProgrammaticScroll) return;

        // --- FORWARD SCROLL (Direction > 0 means Scrolling Down/Forward) ---
        if (direction > 0) {
            // Check each section (except the last one)
            for (let i = 0; i < this.world.sections.length - 1; i++) {
                const currentSection = this.world.sections[i];
                const nextSection = this.world.sections[i + 1];

                // Global Hub Z (End of current section)
                // Camera moves negatively. Hub is at -startZ - length.
                const globalHubZ = -currentSection.startZ - currentSection.length;

                // Trigger Zone: From 6m before wall to the wall
                const zoneStart = globalHubZ + 6.0;
                const zoneEnd = globalHubZ - 1.0;

                if (zPos <= zoneStart && zPos > zoneEnd) {
                    console.log(`[ScrollManager] Forward Auto-Scroll Triggered at Section ${i} End! (Z: ${zPos})`);

                    // Target: Just behind the wall (Start of Next Section + small offset)
                    const targetZ = -nextSection.startZ - 1.5;

                    this.isAutoScrolling = true;
                    this.scrollToZ(targetZ, () => {
                        this.isAutoScrolling = false;
                    });
                    return; // Done
                }
            }
        }

        // --- BACKWARD SCROLL (Direction < 0 means Scrolling Up/Backward) ---
        else if (direction < 0) {
            // Check each section (Start from 1, can't go back from 0)
            for (let i = 1; i < this.world.sections.length; i++) {
                const currentSection = this.world.sections[i];
                const prevSection = this.world.sections[i - 1];

                // Section Start Wall (Entrance)
                // Location: -currentSection.startZ
                const sectionStartZ = -currentSection.startZ;

                // Trigger Zone: Crossing the start wall moving BACKWARDS (towards +Z)
                // Range: [-1.2m, -0.05m] relative to wall (Inside current section)
                const zoneInner = sectionStartZ - 1.2;
                const zoneOuter = sectionStartZ - 0.05;

                if (zPos >= zoneInner && zPos < zoneOuter) {
                    console.log(`[ScrollManager] Backward Auto-Scroll Triggered at Section ${i} Start! (Z: ${zPos})`);

                    // Target: Start of PREVIOUS Section (Entrance of Section i-1)
                    const targetZ = -prevSection.startZ - 2.0;

                    this.isAutoScrolling = true;
                    this.scrollToZ(targetZ, () => {
                        this.isAutoScrolling = false;
                    });
                    return; // Done
                }
            }
        }
    }

    initKeyboard() {
        window.addEventListener('keydown', (e) => {
            const speed = 2; // Movement speed
            let zPos = this.world.camera.position.z;

            switch (e.key.toLowerCase()) {
                case 'arrowup':
                case 'z':
                    e.preventDefault();
                    // Same action as Menu Right Arrow (Next)
                    this.jumpToNextSection();
                    break;
                case 'arrowdown':
                case 's':
                    e.preventDefault();
                    // Same action as Menu Left Arrow (Previous)
                    this.jumpToPreviousSection();
                    break;
                case 'arrowleft':
                case 'q':
                    e.preventDefault();
                    this.lookLeft();
                    return;
                case 'arrowright':
                case 'd':
                    e.preventDefault();
                    this.lookRight();
                    return;
            }
        });
    }

    initTouch() {
        let touchStartX = 0;
        let touchStartY = 0;
        let isDragging = false;
        let initialRotationY = 0;

        window.addEventListener('touchstart', (e) => {
            touchStartX = e.touches[0].screenX;
            touchStartY = e.touches[0].screenY;
            isDragging = true;
            initialRotationY = this.world.camera.rotation.y;
        }, { passive: true });

        window.addEventListener('touchmove', (e) => {
            if (!isDragging) return;

            const touchCurrentX = e.touches[0].screenX;
            const touchCurrentY = e.touches[0].screenY;

            const deltaX = touchCurrentX - touchStartX;
            const deltaY = touchCurrentY - touchStartY;

            // FIX: If movement is primarily vertical (scrolling), ignore rotation
            // This prevents accidental camera turning when just trying to scroll down
            if (Math.abs(deltaY) > Math.abs(deltaX)) return;

            // Map Delta X to Rotation Angle
            const sensitivity = 0.005;
            let targetRotation = initialRotationY + (deltaX * sensitivity);

            // Clamp Rotation
            const maxAngle = Math.PI / 3; // 60 degrees
            if (targetRotation > maxAngle) targetRotation = maxAngle;
            if (targetRotation < -maxAngle) targetRotation = -maxAngle;

            // Apply directly
            this.world.camera.rotation.y = targetRotation;

        }, { passive: true });

        window.addEventListener('touchend', () => {
            isDragging = false;
        }, { passive: true });
    }

    // Removed handleSwipe as we now use direct drag control.
    // lookLeft/Right leftovers for Keyboard Use Only (checked below)

    lookRight(isMobile = false) {
        // Keyboard Only now (or if called programmatically)
        const angle = Math.PI / 3;
        // ... (Keyboard logic remains valid for Arrow Keys on Desktop)
        // Ensure this doesn't conflict. Mobile touch no longer calls this.
        if (this.world.camera.rotation.y > 0.1) {
            this.world.rotateCamera(0, isMobile);
        } else if (this.world.camera.rotation.y < -0.1) {
            this.world.rotateCamera(0, isMobile);
        } else {
            this.world.rotateCamera(-angle, isMobile);
        }
    }

    lookLeft(isMobile = false) {
        const angle = Math.PI / 3;
        if (this.world.camera.rotation.y < -0.1) {
            this.world.rotateCamera(0, isMobile);
        } else if (this.world.camera.rotation.y > 0.1) {
            this.world.rotateCamera(0, isMobile);
        } else {
            this.world.rotateCamera(angle, isMobile);
        }
    }

    jumpToPreviousSection() {
        if (!this.world.sections || this.world.sections.length === 0) return;

        const currentZ = this.world.camera.position.z;
        let targetZ = null;

        // Find the closest section start that is "behind" us (Positive direction)
        // We want a startZ that is > currentZ + small buffer
        // If we are deep in a section, this will be that section's start.
        // If we are at the start, this will be the previous section's start.

        // Iterate in reverse to find the first one that satisfies the condition
        for (let i = this.world.sections.length - 1; i >= 0; i--) {
            const section = this.world.sections[i];
            const sectionGlobalStart = -section.startZ;

            if (sectionGlobalStart > currentZ + 1.0) {
                targetZ = sectionGlobalStart;
                break; // Found the nearest one behind us
            }
        }

        // Special case: if we are at the very start of the world (e.g. Z=5) and press left, do nothing or stay at start.
        // The loop above might not find anything if we are > -10. 
        // But usually we are negative.

        if (targetZ !== null) {
            this.scrollToZ(targetZ);
        }
    }

    jumpToNextSection() {
        if (!this.world.sections || this.world.sections.length === 0) return;

        const currentZ = this.world.camera.position.z;
        let targetZ = null;

        // Find the closest section start that is "ahead" of us (Negative direction)
        // We want a startZ that is < currentZ - small buffer

        for (const section of this.world.sections) {
            const sectionGlobalStart = -section.startZ;

            if (sectionGlobalStart < currentZ - 1.0) {
                targetZ = sectionGlobalStart;
                break; // Found the nearest one ahead
            }
        }

        if (targetZ !== null) {
            this.scrollToZ(targetZ);
        }
    }

    scrollToZ(targetZ, onComplete) {
        this.isProgrammaticScroll = true;
        const totalDistance = this.world.totalLength;
        const startZ = 5;
        const endZ = -totalDistance + 1;

        // zPos = startZ - (progress * (startZ - endZ))
        // progress = (startZ - zPos) / (startZ - endZ)

        const progress = (startZ - targetZ) / (startZ - endZ);
        const maxScroll = document.body.scrollHeight - window.innerHeight;
        const scrollPos = progress * maxScroll;

        // Use GSAP for smooth programmatic scroll if needed, 
        // or just native window.scrollTo with behavior smooth.
        // Native smooth scroll doesn't have a callback.

        // Let's use simple timeout or native callback workaround?
        // Since we are scrolling the WINDOW, we can't easily tween it with GSAP 
        // unless we use ScrollToPlugin (not imported).

        window.scrollTo({ top: scrollPos, behavior: 'smooth' });

        if (onComplete) {
            // Rough estimation of completion or just reset flag after delay
            setTimeout(() => {
                this.isProgrammaticScroll = false;
                onComplete();
            }, 1500); // 1.5s usually enough for smooth scroll
        } else {
            setTimeout(() => {
                this.isProgrammaticScroll = false;
            }, 1500);
        }
    }

    handleDeepLink() {
        const hash = window.location.hash.replace('#', '');
        if (!hash) return;

        const section = this.world.sections.find(s => s.id === hash);
        if (section) {
            // Scroll to the start of this section
            const targetZ = -section.startZ;
            // We use scrollToZ but maybe without smooth behavior for initial load?
            // Let's use the same helper but force instant if needed. 
            // For now, smooth is fine or we can just set it.

            const totalDistance = this.world.totalLength;
            const startZ = 5;
            const endZ = -totalDistance + 20;
            const progress = (startZ - targetZ) / (startZ - endZ);
            const maxScroll = document.body.scrollHeight - window.innerHeight;

            window.scrollTo(0, progress * maxScroll);
        }
    }
}
